import {
  DateFormats,
  MedicationStatusEnum,
  SortOrder,
} from 'infrastructure/enums';
import { DateFormatter, isCcmModule } from 'infrastructure/functions';
import {
  useGetMedication,
  useAddMedication,
  useEditMedication,
  useDeleteMedication,
  MEDICATION_PER_PAGE,
} from 'infrastructure/hooks/ccm';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isCcmActionAllowedStatus } from 'utils/userTypeHelper';
import useUser from 'utils/useUser';

import type { SortState } from 'infrastructure/types';
import type { ModuleType } from 'infrastructure/enums';
import type {
  IAddMedicationData,
  IMedicationForUpdate,
} from 'infrastructure/interfaces';
import type { FormikHelpers } from 'formik';
import type { Values } from 'components/molecules/admin-panel/forms/medication/validation-schema';

interface Props {
  patientGuid: string;
  module: ModuleType;
}

export const SORT_OPTIONS = [
  {
    value: SortOrder.DESC,
    label: 'Latest',
  },
  {
    value: SortOrder.ASC,
    label: 'Oldest',
  },
];

const ALL_STATUS_VALUE = 'all';

export const useMedication = ({ patientGuid, module }: Props) => {
  const { t } = useTranslation();

  const [status, setStatus] = useState<string | boolean>(ALL_STATUS_VALUE);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [documentationTimeSort, setDocumentationTimeSort] = useState<SortState>(
    SortOrder.DESC,
  );
  const [itemPerPage, setItemPerPage] = useState(MEDICATION_PER_PAGE);
  const [modifyItemGuid, setModifyItemGuid] = useState<string>();
  const [medicationForUpdate, setMedicationForUpdate] =
    useState<IMedicationForUpdate>();

  const statusFilter: boolean | undefined =
    status === ALL_STATUS_VALUE ? undefined : (status as boolean);

  const { medication, isLoading, isFetching, refetch, remove } =
    useGetMedication({
      patientGuid,
      meta: {
        page,
        items: itemPerPage,
      },
      isActive: statusFilter,
      documentationTimeSort,
    });

  const OPTIONS = useMemo(() => {
    return [
      {
        value: ALL_STATUS_VALUE,
        label: `${MedicationStatusEnum.ALL} (${medication?.total ?? 0})`,
      },
      {
        value: true,
        label: `${MedicationStatusEnum.ACTIVE} (${medication?.active ?? 0})`,
      },
      {
        value: false,
        label: `${MedicationStatusEnum.INACTIVE} (${
          medication?.inactive ?? 0
        })`,
      },
    ];
  }, [medication?.total, medication?.active, medication?.inactive]);

  const { addMedication, addLoading } = useAddMedication();
  const { editMedication, editLoading } = useEditMedication({
    page,
    items: itemPerPage,
    isActive: statusFilter,
  });
  const { deleteMedication, deleteLoading } = useDeleteMedication({
    page,
    items: itemPerPage,
    isActive: statusFilter,
  });
  const { patientInfo, loading } = usePatientInfo({ patientGuid });
  const disableButton = !isCcmActionAllowedStatus(patientInfo?.ccmStatus);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { Alert, showAlert } = useAlert();
  const { isPatient } = useUser();

  const toggleOpen = () => setOpen((prev) => !prev);

  const formatDate = (date: Date | string) => {
    return DateFormatter({
      date,
      format: DateFormats['MMM dd yyyy'],
    });
  };

  const onCancelForm = () => {
    setMedicationForUpdate(undefined);
    setOpen(false);
  };

  const onSubmit = (values: Values, formikHelpers: FormikHelpers<Values>) => {
    if (!values.name) return;

    const { unit, ...rest } = values;

    const dosage = (rest.dosage ?? '').trim();
    const doseText = dosage && unit ? `${dosage} ${unit}` : dosage;

    const newMedicationData: IAddMedicationData = {
      ...rest,
      dosage: doseText,
      patientGuid,
      startDate: rest?.startDate ? formatDate(rest.startDate) : undefined,
      endDate: rest?.endDate ? formatDate(rest.endDate) : undefined,
    };

    if (medicationForUpdate) {
      editMedication(newMedicationData, {
        onSuccess: () => {
          onCancelForm();
          formikHelpers.resetForm();
          refetch();
        },
      });
      return;
    }

    addMedication(newMedicationData, {
      onSuccess: () => {
        onCancelForm();
        formikHelpers.resetForm();
        refetch();
      },
    });
  };

  const scrollToTop = () => {
    const element = document.getElementById('medication');
    if (element) {
      const yCoordinate =
        element.getBoundingClientRect().top + window.scrollY - 110;
      window.scrollTo({ top: yCoordinate, behavior: 'smooth' });
    }
  };

  const handleEdit = (item: IMedicationForUpdate) => {
    setMedicationForUpdate(item);
    setModifyItemGuid(item.guid);
    toggleOpen();
  };

  const handleDelete = async (guid: string) => {
    const delRes = await showAlert({
      title: t('confirm.deleteMedication'),
      message: `${t('confirm.confirmDeleteMedication')}\n${t(
        'confirm.deleteMedicationMessage',
      )}`,
      type: 'danger',
    });
    if (delRes) {
      setModifyItemGuid(guid);
      deleteMedication(guid);
    }
  };

  const modalTitle = medicationForUpdate
    ? t('labels.editMedication')
    : t('labels.addMedication');

  return {
    modalTitle,
    Alert,
    open,
    modifyItemGuid,
    medication,
    isLoading,
    isFetching,
    addLoading,
    editLoading,
    deleteLoading,
    loading,
    patientInfo,
    disableButton: isCcmModule(module) ? disableButton : isPatient,
    wrapperRef,
    OPTIONS,
    status,
    page,
    itemPerPage,
    documentationTimeSort,
    setDocumentationTimeSort,
    handleDelete,
    handleEdit,
    scrollToTop,
    onSubmit,
    setStatus,
    setPage,
    setItemPerPage,
    toggleOpen,
    onCancelForm,
    remove,
    medicationForUpdate,
  };
};
